import { useEffect, useRef, useState } from "react";
import { api } from "../api/client.js";
import "../Style/CandleRequestModal.css";

const PRICE_NOTE = "Tax included";

const formatCandlePrice = (candle) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(candle.price);

export default function CandleRequestModal({
  title = "Request Candles",
  filters,
  emptyMessage = "No candles are available yet.",
  onClose,
  onOrder,
}) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const [candles, setCandles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");

    api.candles
      .list(filters, { signal: controller.signal })
      .then((response) => {
        setCandles(response.data || []);
        setError("");
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setCandles([]);
          setError("Candles could not be loaded. Please try again.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [filters]);

  const toggleCandle = (candleId) => {
    setActionError("");
    setSelectedIds((current) =>
      current.includes(candleId)
        ? current.filter((id) => id !== candleId)
        : [...current, candleId],
    );
  };

  const orderSelected = () => {
    const selectedCandles = candles.filter((candle) =>
      selectedIds.includes(candle.id),
    );
    const errorMessage = onOrder(selectedCandles);
    if (errorMessage) setActionError(errorMessage);
  };

  return (
    <div
      className="candle-request-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="candle-request-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="candle-request-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="candle-request-header">
          <div>
            <p>Up &amp; Away </p>
            <h2 id="candle-request-title">{title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close candles">
            &times;
          </button>
        </header>

        {loading && (
          <p className="candle-request-message" role="status">
            Loading candles...
          </p>
        )}

        {!loading && error && (
          <p className="candle-request-error" role="alert">
            {error}
          </p>
        )}

        {!loading && !error && candles.length > 0 && (
          <div className="candle-request-grid">
            {candles.map((candle) => {
              const selected = selectedIds.includes(candle.id);
              const isOutOfStock = candle.inStock === false;
              return (
                <button
                  type="button"
                  className={`candle-request-card${selected ? " selected" : ""}${
                    isOutOfStock ? " is-out-of-stock" : ""
                  }`}
                  key={candle.id}
                  onClick={() => toggleCandle(candle.id)}
                  aria-pressed={selected}
                  disabled={isOutOfStock}
                >
                  <span className="candle-request-color">
                    <i
                      style={{ backgroundColor: candle.color?.value }}
                      aria-hidden="true"
                    />
                    {candle.color?.name}
                  </span>
                  <strong>{candle.fragranceNote}</strong>
                  {isOutOfStock && <small>Out of stock</small>}
                  <small>{formatCandlePrice(candle)}</small>
                  <small className="product-price-note">{PRICE_NOTE}</small>
                  <em>{candle.mood}</em>
                  {candle.products?.length > 0 && (
                    <small>
                      {candle.products
                        .map((product) => product.name)
                        .join(", ")}
                    </small>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {!loading && !error && candles.length === 0 && (
          <p className="candle-request-message">{emptyMessage}</p>
        )}

        {actionError && (
          <p className="candle-request-error" role="alert">
            {actionError}
          </p>
        )}

        <div className="candle-request-actions">
          <button
            type="button"
            className="candle-request-secondary"
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="button"
            className="candle-request-primary"
            onClick={orderSelected}
            disabled={selectedIds.length === 0}
          >
            Order selected candles
          </button>
        </div>
      </section>
    </div>
  );
}
